import React, { ReactNode } from 'react';
import { Button } from './button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './drawer';
import { Sheet, SheetContent } from './sheet';

export interface DrawerFormProps<T = any> {
  children?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSubmit?: (data: T) => void;
  submitText?: string;
  cancelText?: string;
  loading?: boolean;
  disabled?: boolean;
  showFooter?: boolean;
  footerContent?: ReactNode;
  title?: string;
  description?: string;
  formRef?: React.RefObject<HTMLFormElement>;
  width?: 'sm' | 'md' | 'lg' | 'xl';
  direction?: 'right' | 'left';
}

export function DrawerForm<T = any>({
  children,
  open,
  onOpenChange,
  submitText = 'Submit',
  cancelText = 'Cancel',
  loading = false,
  disabled = false,
  showFooter = true,
  footerContent,
  title,
  description,
  width = 'md',
  direction = 'right',
}: DrawerFormProps<T>) {
  const handleSubmit = () => {
    const formElement = document.querySelector(
      '[data-drawer-form="true"]'
    ) as HTMLFormElement;
    if (formElement) {
      const submitEvent = new Event('submit', {
        bubbles: true,
        cancelable: true,
      });
      formElement.dispatchEvent(submitEvent);
    }
  };

  if (direction === 'left' || direction === 'right') {
    const widthClasses = {
      sm: 'w-full max-w-sm',
      md: 'w-full max-w-md',
      lg: 'w-full max-w-lg',
      xl: 'w-full max-w-xl',
    };

    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side={direction} className={widthClasses[width]}>
          <div className="flex flex-col h-full">
            {(title || description) && (
              <div className="flex-shrink-0">
                {title && <h3 className="text-lg font-semibold">{title}</h3>}
                {description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {description}
                  </p>
                )}
              </div>
            )}

            <div className="flex-1 overflow-y-auto py-4">{children}</div>

            {showFooter && (
              <div className="flex-shrink-0 pt-4 border-t">
                {footerContent || (
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={() => onOpenChange?.(false)}
                      disabled={loading}
                    >
                      {cancelText}
                    </button>
                    <Button
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading || disabled}
                    >
                      {loading ? 'Submitting...' : submitText}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="h-auto max-h-[85vh]">
        <div className="flex flex-col max-h-[85vh]">
          {(title || description) && (
            <DrawerHeader className="flex-shrink-0 px-4 pt-4">
              {title && <DrawerTitle>{title}</DrawerTitle>}
              {description && (
                <DrawerDescription>{description}</DrawerDescription>
              )}
            </DrawerHeader>
          )}

          <div className="flex-1 overflow-y-auto p-4">{children}</div>

          {showFooter && (
            <DrawerFooter className="flex-shrink-0 p-4">
              {footerContent || (
                <div className="flex justify-end space-x-3">
                  <DrawerClose asChild>
                    <Button variant="outline" disabled={loading}>
                      {cancelText}
                    </Button>
                  </DrawerClose>
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading || disabled}
                  >
                    {loading ? 'Submitting...' : submitText}
                  </Button>
                </div>
              )}
            </DrawerFooter>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
